import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'
import Popconfirm from '../components/Popconfirm'
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  NotificationOutlined,
  SearchOutlined,
  SendOutlined
} from '@ant-design/icons'
import * as api from '../lib/api'
import { formatDate } from '../lib/dateFormat'
import { useI18n } from '../i18n/I18nProvider'
import AppraisalWorkflowCard from './AppraisalWorkflowCard'

const { Title, Text } = Typography
const statusColors = { draft: 'default', active: 'green', closed: 'blue' }
const PROCESS_OVERVIEW_EMAILS_ENABLED = process.env.REACT_APP_PROCESS_OVERVIEW_EMAILS_ENABLED === 'true'

export default function GlobalHRDashboard({ me } = {}) {
  const { t } = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [page, setPage] = useState(1)
  const [sendingProcess, setSendingProcess] = useState({})
  const [sendingInvites, setSendingInvites] = useState({})
  const sessionsTableRef = useRef(null)
  const selectedRegionCode = searchParams.get('region')

  const roles = me?.roles || []
  const canSendEmails = Boolean(
    me?.is_staff ||
    me?.is_superuser ||
    roles.includes('frontend_superadmin') ||
    roles.includes('global_hr_superadmin') ||
    roles.includes('global_hr_tti_milwaukee_asia')
  )

  async function sendProcessOverviewEmails(id) {
    setSendingProcess(prev => ({ ...prev, [id]: true }))
    try {
      const res = await api.sendSessionProcessOverviewEmails(id)
      window.alert(t('dashboard.alerts.process_overview_sent_with_senior_approvers', 'Introductory emails sent to {{employees}} employee(s), {{managers}} Approver 1 participant(s), and {{seniorApprovers}} Approver 2/3 participant(s)', {
        employees: res.sent_employees,
        managers: res.sent_managers,
        seniorApprovers: res.sent_senior_approvers
      }))
    } catch (e) {
      message.error(e.message || t('dashboard.messages.process_emails_failed', 'Unable to send process overview emails'))
    } finally {
      setSendingProcess(prev => ({ ...prev, [id]: false }))
    }
  }

  async function sendInvitationEmails(id) {
    setSendingInvites(prev => ({ ...prev, [id]: true }))
    try {
      const res = await api.triggerSessionEmails(id)
      window.alert(t('dashboard.alerts.emails_sent', '{{count}} email(s) sent', { count: res.sent }))
    } catch (e) {
      message.error(e.message || t('dashboard.messages.invitation_emails_failed', 'Unable to send appraisal invitation emails'))
    } finally {
      setSendingInvites(prev => ({ ...prev, [id]: false }))
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 350)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (overview) setTableLoading(true)
    api.getGlobalHROverview({
      region: selectedRegionCode,
      status: statusFilter,
      q: debouncedSearch,
      page,
      pageSize: 10
    })
      .then(setOverview)
      .catch(err => setError(err.message))
      .finally(() => {
        setLoading(false)
        setTableLoading(false)
      })
  }, [debouncedSearch, page, selectedRegionCode, statusFilter])

  const selectedRegion = useMemo(
    () => overview?.regions?.find(region => region.code === selectedRegionCode) || null,
    [overview, selectedRegionCode]
  )

  const selectRegion = code => {
    const next = new URLSearchParams(searchParams)
    if (code) next.set('region', code)
    else next.delete('region')
    setSearchParams(next)
    setSearch('')
    setStatusFilter('active')
    setPage(1)
  }

  const sessionColumns = [
    {
      title: t('global_hr.sessions.name', 'Session'),
      dataIndex: 'name',
      render: (name, record) => (
        <Space direction='vertical' size={0}>
          <Link to={`/sessions/${record.id}`} style={{ fontWeight: 600 }}>{name}</Link>
          <Text type='secondary' style={{ fontSize: 12 }}>{record.template_name}</Text>
        </Space>
      )
    },
    ...(!selectedRegion
      ? [{
        title: t('global_hr.sessions.region', 'Region'),
        dataIndex: 'region_name',
        render: (name, record) => (
          <Button type='link' style={{ padding: 0 }} onClick={() => selectRegion(record.region_code)}>
            {name}
          </Button>
        )
      }]
      : []),
    {
      title: t('global_hr.sessions.organization', 'Organization'),
      dataIndex: 'organization_name'
    },
    {
      title: t('global_hr.sessions.period', 'Period'),
      render: (_, record) => `${formatDate(record.start_date)} - ${formatDate(record.end_date)}`
    },
    {
      title: t('global_hr.sessions.status', 'Status'),
      dataIndex: 'status',
      render: value => <Tag color={statusColors[value]}>{value}</Tag>
    },
    {
      title: t('global_hr.sessions.appraisals', 'Appraisals'),
      dataIndex: 'appraisal_count',
      align: 'right'
    },
    {
      title: t('global_hr.sessions.progress', 'Progress'),
      width: 180,
      render: (_, record) => <Progress percent={record.completion_percent} size='small' strokeColor='#0080C1' />
    },
    {
      title: '',
      key: 'view',
      width: canSendEmails ? 190 : 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size={8} style={{ width: '100%', justifyContent: 'flex-end' }}>
          {PROCESS_OVERVIEW_EMAILS_ENABLED && canSendEmails && (
            <Popconfirm
              title={t('dashboard.confirm.process_overview_emails.title', 'Send process overview emails?')}
              description={t('dashboard.confirm.process_overview_emails_with_senior_approvers.description', 'This sends the relevant process overview email to employees and Approvers 1, 2, and 3.')}
              okText={t('dashboard.actions.send_process_overview_emails', 'Send Process Overview Emails')}
              cancelText={t('dashboard.actions.cancel', 'Cancel')}
              onConfirm={() => sendProcessOverviewEmails(record.id)}
            >
              <Tooltip title={t('dashboard.actions.send_process_overview_emails', 'Send Process Overview Emails')}>
                <Button icon={<NotificationOutlined />} loading={Boolean(sendingProcess[record.id])} aria-label={t('dashboard.actions.send_process_overview_emails', 'Send Process Overview Emails')} />
              </Tooltip>
            </Popconfirm>
          )}
          <Link to={`/sessions/${record.id}`}>
            <Button type='primary'>{t('global_hr.sessions.view', 'View')}</Button>
          </Link>
          {canSendEmails && (
            <Popconfirm
              title={t('dashboard.confirm.send_appraisal_invitations.title', 'Send appraisal invitation emails?')}
              description={t('dashboard.confirm.send_appraisal_invitations.description', 'This sends an appraisal invitation to each pending employee and activates the session if it is still in draft.')}
              okText={t('dashboard.actions.send_appraisal_invitations', 'Send Appraisal Invitation Emails')}
              cancelText={t('dashboard.actions.cancel', 'Cancel')}
              onConfirm={() => sendInvitationEmails(record.id)}
            >
              <Tooltip title={t('dashboard.actions.send_appraisal_invitations', 'Send Appraisal Invitation Emails')}>
                <Button icon={<SendOutlined />} loading={Boolean(sendingInvites[record.id])} aria-label={t('dashboard.actions.send_appraisal_invitations', 'Send Appraisal Invitation Emails')} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  if (loading) return <div style={{ padding: 80, textAlign: 'center' }}><Spin size='large' /></div>
  if (error) return <Alert type='error' message={error} showIcon />

  return (
    <div>
      {selectedRegion && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => selectRegion(null)}
          style={{ marginBottom: 16 }}
        >
          {t('global_hr.regions.back', 'All regions')}
        </Button>
      )}
      <AppraisalWorkflowCard
        title={t('global_hr.title', 'Performance Overview')}
        statsOnly
        stats={[
          {
            label: t('global_hr.metrics.regions', 'Regions'),
            value: overview.totals.regions,
            color: 'var(--tti-info)',
            icon: <GlobalOutlined style={{ color: 'var(--tti-info)', fontSize: 18 }} />
          },
          {
            label: t('global_hr.metrics.active_sessions', 'Active sessions'),
            value: overview.totals.active_sessions,
            color: 'var(--tti-info)',
            icon: <CalendarOutlined style={{ color: 'var(--tti-info)', fontSize: 18 }} />
          },
          {
            label: t('global_hr.metrics.appraisals', 'Appraisals'),
            value: overview.totals.appraisals,
            color: 'var(--tti-info)',
            icon: <ApartmentOutlined style={{ color: 'var(--tti-info)', fontSize: 18 }} />
          },
          {
            label: t('global_hr.metrics.completed', 'Completed'),
            value: `${overview.totals.completion_percent}%`,
            color: 'var(--tti-success)',
            icon: <CheckCircleOutlined style={{ color: 'var(--tti-success)', fontSize: 18 }} />
          }
        ]}
      />

      {!selectedRegion && (
        <>
          <Title level={4}>{t('global_hr.regions.title', 'Regional Workspaces')}</Title>
          <Row gutter={[18, 18]} style={{ marginBottom: 24 }}>
            {overview.regions.map(region => (
              <Col xs={24} md={12} xl={8} key={region.id}>
                <Card
                  ref={sessionsTableRef}
                  hoverable
                  onClick={() => selectRegion(region.code)}
                  styles={{ body: { padding: 22 } }}
                  style={{ borderTop: '4px solid #0080C1', height: '100%' }}
                >
                  <Space align='start' style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <Title level={4} style={{ margin: '4px 0 2px' }}>{region.name}</Title>
                      <Text type='secondary'>{region.session_count} sessions</Text>
                    </div>
                    <Button type='text' shape='circle' icon={<ArrowRightOutlined />} />
                  </Space>
                  <Row gutter={12} style={{ marginTop: 22 }}>
                    <Col span={12}><Statistic title='Active' value={region.active_session_count} valueStyle={{ fontSize: 22 }} /></Col>
                    <Col span={12}><Statistic title='Appraisals' value={region.appraisal_count} valueStyle={{ fontSize: 22 }} /></Col>
                  </Row>
                  <Progress percent={region.completion_percent} strokeColor={{ '0%': '#0080C1', '100%': '#52C41A' }} style={{ marginTop: 14 }} />
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      <Card
        ref={sessionsTableRef}
      >
        <Title level={4}>
          {selectedRegion
            ? (
              <Space>
                <GlobalOutlined style={{ color: '#0080C1' }} />
                <span>{selectedRegion.name}</span>
              </Space>
            )
            : t('global_hr.sessions.all', 'All Sessions')}
        </Title>
        {selectedRegion && (
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={12} md={6}><Statistic title='Sessions' value={selectedRegion.session_count} /></Col>
            <Col xs={12} md={6}><Statistic title='Active sessions' value={selectedRegion.active_session_count} /></Col>
            <Col xs={12} md={6}><Statistic title='Appraisals' value={selectedRegion.appraisal_count} /></Col>
            <Col xs={12} md={6}><Statistic title='Completion' value={selectedRegion.completion_percent} suffix='%' /></Col>
          </Row>
        )}
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder='Search sessions, templates, organizations, or regions'
            value={search}
            onChange={event => setSearch(event.target.value)}
            style={{ width: 380, maxWidth: '100%' }}
          />
          <Select
            value={statusFilter}
            onChange={value => {
              setStatusFilter(value)
              setPage(1)
            }}
            style={{ width: 150 }}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'active', label: 'Active' },
              { value: 'closed', label: 'Closed' }
            ]}
          />
        </Space>
        <Table
          columns={sessionColumns}
          dataSource={overview.sessions.results}
          rowKey='id'
          loading={tableLoading}
          pagination={{
            current: overview.sessions.page,
            pageSize: overview.sessions.page_size,
            total: overview.sessions.count,
            hideOnSinglePage: true,
            showTotal: total => `${total} sessions`,
            onChange: nextPage => {
              setPage(nextPage)
              window.setTimeout(() => {
                sessionsTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 0)
            }
          }}
          scroll={{ x: 950 }}
          locale={{ emptyText: <Empty description='No sessions match these filters.' /> }}
        />
      </Card>
    </div>
  )
}
